import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '@supabase/auth-helpers-react';
import { useData } from '../../hooks/useData';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../utils/errorLogging';
import { useApp } from '../../contexts/AppContext';
import { TABLES } from '../../constants';
import pageStyles from '../../styles/pages.module.css';
import SpaceTopBar from './SpaceTopBar';
import SpaceItemsSection from './Sections/Items/SpaceItemsSection';

const LAST_SPACE_KEY = 'lastSelectedSpace';
const KLALI_SPACE_ID = '__klali__';
const CONSTRUCTION_SPACE_ID = '__construction__';

const isVirtualSpace = id => id === KLALI_SPACE_ID || id === CONSTRUCTION_SPACE_ID;

function SpacesPage() {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const { logError } = useErrorLogging();
  const { currentProject } = useApp();
  const session = useSession();

  // Fetch floors
  const {
    data: allFloors,
    error: floorsError,
    isLoading: floorsLoading,
  } = useData(TABLES.PROJECT_FLOORS, {
    enabled: Boolean(currentProject?.id),
    realtime: true,
    select: '*',
    filter: { project_id: currentProject?.id },
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Error fetching floors',
      });
    },
  });

  const floorIds = useMemo(() => allFloors?.map(f => f.id) || [], [allFloors]);

  // Fetch spaces
  const {
    data: spaces,
    error: spacesError,
    isLoading: isLoadingSpaces,
    softDeleteSpace,
  } = useData(TABLES.PROJECT_SPACES, {
    enabled: Boolean(currentProject?.id) && Boolean(session?.user) && floorIds.length > 0,
    select: '*, project_floors!inner(*)',
    filter: { floor_id: floorIds },
    realtime: true,
    onError: error => {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_FETCH,
        message: 'Error fetching spaces',
      });
    },
  });

  // Fetch master_spaces for type note in top bar
  const { data: masterSpaces } = useData(TABLES.MASTER_SPACES, {
    select: 'id, name',
    realtime: false,
  });

  // Navigate to first space if none selected
  useEffect(() => {
    if ((!spaceId || spaceId === 'items' || spaceId === 'windows') && spaces?.length > 0) {
      navigate(`/spaces/${spaces[0].id}`);
    }
  }, [spaceId, spaces, navigate]);

  // Handle default space selection
  useEffect(() => {
    if (!spaceId && !isLoadingSpaces && spaces?.length > 0) {
      const lastSpaceId = localStorage.getItem(LAST_SPACE_KEY);
      const defaultSpace = spaces.find(s => s.id === lastSpaceId) || spaces[0];
      localStorage.setItem(LAST_SPACE_KEY, defaultSpace.id);
      navigate(`/spaces/${defaultSpace.id}`);
    }
  }, [spaceId, spaces, isLoadingSpaces, navigate]);

  // Store current space in localStorage
  useEffect(() => {
    if (spaceId) {
      localStorage.setItem(LAST_SPACE_KEY, spaceId);
    }
  }, [spaceId]);

  // Handle delete space
  const handleDeleteSpace = useCallback(async () => {
    if (!spaceId || isVirtualSpace(spaceId)) return;

    if (!window.confirm('האם אתה בטוח שברצונך למחוק את החלל? הפריטים שהוספת לא יימחקו לצמיתות.')) {
      return;
    }

    try {
      await softDeleteSpace(spaceId);
      const remainingSpaces = (spaces || []).filter(s => s.id !== spaceId);
      if (remainingSpaces.length > 0) {
        navigate(`/spaces/${remainingSpaces[0].id}`);
      } else {
        navigate(`/spaces/${KLALI_SPACE_ID}`);
      }
    } catch (error) {
      logError(error, {
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA_DELETE,
        message: `Error deleting space ${spaceId}`,
      });
      alert('שגיאה במחיקת החלל');
    }
  }, [spaceId, softDeleteSpace, spaces, navigate, logError]);

  // Loading / error states
  if (spacesError || floorsError) {
    return <div className={pageStyles.emptyState}>שגיאה בטעינת הנתונים</div>;
  }

  if (isLoadingSpaces || floorsLoading) {
    return <div className={pageStyles.loadingContainer}>טוען...</div>;
  }

  const currentSpace = spaces?.find(s => s.id === spaceId);

  return (
    <div className={pageStyles.pageContainer}>
      {/* Top bar replaces PageToolbar + info tab */}
      <SpaceTopBar
        spaces={spaces}
        allFloors={allFloors}
        currentSpaceId={spaceId}
        onSpaceChange={value => navigate(`/spaces/${value}`)}
        onDeleteSpace={handleDeleteSpace}
        spaceData={currentSpace}
        masterSpaces={masterSpaces}
      />

      {/* Content — SpaceItemsSection handles everything now (vendor cards + windows) */}
      {spaceId === KLALI_SPACE_ID && <SpaceItemsSection spaceId={null} space={null} isKlali />}

      {spaceId === CONSTRUCTION_SPACE_ID && (
        <SpaceItemsSection spaceId={null} space={null} isConstruction />
      )}

      {spaceId && !isVirtualSpace(spaceId) && (
        <SpaceItemsSection spaceId={spaceId} space={currentSpace} />
      )}
    </div>
  );
}

export default SpacesPage;
